import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import BackOfficeLayout from "../../../../components/backoffice-layout";
import PaginatorComponent from "../../../../components/paginator";
import DateRangePickerComponent from "../../../../components/date-range-picker";
import LoadingModal from "../../../../components/loading-modal";
import SearchableField from "../../../../components/searchable-field";
import CoreAPI from "../../../../services/core";
import { useOpenModalTokenExpired } from "../../../../contexts";

const TYPE_LIST = [
    { id: "Place", label: "Place (Touch Point)" },
    { id: "Event", label: "Event" },
    { id: "Service", label: "Service" },
];

function rangeForPreset(preset) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    switch (preset) {
        case "this_month":
            return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0) };
        case "last_month":
            return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0) };
        case "this_year":
            return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
        case "last_year":
            return { start: new Date(y - 1, 0, 1), end: new Date(y - 1, 11, 31) };
        default:
            return { start: null, end: null };
    }
}

const Container = styled.div`
    width: calc(100% - 32px);
    background-color: #FFFFFF;
    padding: 40px 16px;
    border-radius: 16px;

    .header-box {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .header-label {
            width: fit-content;
            color: #25368D;
            font-size: 20px;
            font-weight: 600;
            line-height: 29.9px;
            text-align: left;
            padding: 2px;
            border-width: 0px 0px 3px 0px;
            border-style: solid;
            border-color: #25368D;
        }

        .export-btn {
            background-color: #25368D;
            color: #FFFFFF;
            font-size: 14px;
            font-weight: 600;
            padding: 12px 24px;
            border-radius: 30px;
            border: none;
            cursor: pointer;
            transition: opacity 0.2s ease;

            &:hover { opacity: 0.85; }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }

    .type-tabs {
        display: flex;
        gap: 8px;
        margin-bottom: 24px;
        border-bottom: 1px solid #E5E5E5;
    }

    .type-tab {
        padding: 12px 20px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        color: #505050;
        border-bottom: 3px solid transparent;
        transition: all 0.2s ease;

        &:hover { color: #25368D; }

        &.active {
            color: #25368D;
            font-weight: 600;
            border-bottom-color: #25368D;
        }
    }

    .filter-bar {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 36px;

        .filters-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 16px;
            align-items: end;
        }
    }

    .preset-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;

        .preset-label {
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            margin-right: 4px;
        }

        .preset-btn {
            padding: 6px 14px;
            border-radius: 20px;
            border: 1px solid #E5E5E5;
            background-color: #FFFFFF;
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover { border-color: #25368D; color: #25368D; }
            &.active {
                background-color: #25368D;
                color: #FFFFFF;
                border-color: #25368D;
            }
        }
    }

    .data-box {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
`;

const Table = styled.div`
    width: 100%;
    border: 1px solid #F2F2F2;
    border-radius: 12px;
    overflow: hidden;

    .row {
        display: grid;
        grid-template-columns: 70px 2.5fr 1fr 1.4fr 1.4fr;
        align-items: center;
        padding: 14px 16px;
        border-bottom: 1px solid #F2F2F2;
        font-size: 14px;
        color: #2A2A2A;
        gap: 8px;

        &.header {
            background-color: #FBE25033;
            color: #25368D;
            font-weight: 600;
        }

        &:last-child { border-bottom: none; }
    }

    .cell-count {
        font-weight: 600;
        color: #25368D;
    }

    .empty-box {
        padding: 48px 16px;
        text-align: center;
        color: #B5B5B5;
        font-size: 14px;
    }
`;

function RatingSummaryPage() {
    const navigate = useNavigate();
    const { setOpenModalTokenExpired } = useOpenModalTokenExpired();

    const [evaluationType, setEvaluationType] = useState("Place");
    const [listForm, setListForm] = useState([]);
    const [listFormSelect, setListFormSelect] = useState([]);
    const [listLocation, setListLocation] = useState([]);
    const [listLocationSelect, setListLocationSelect] = useState([]);
    const [listProvince, setListProvince] = useState([]);
    const [listProvinceSelect, setListProvinceSelect] = useState([]);
    const [dateRange, setDateRange] = useState(() => rangeForPreset("this_month"));
    const [activePreset, setActivePreset] = useState("this_month");

    const [pageOption, setPageOption] = useState({
        limit: 50, page: 1, total_item: 0, total_page: 1,
    });
    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [sizeWarning, setSizeWarning] = useState(null);
    // sizeWarning shape: { location_count, estimated_pages, estimated_seconds, onConfirm, onCancel }

    useEffect(() => {
        CoreAPI.reportHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.evaluationFormHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.locationHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        onInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onInit() {
        let _profile = localStorage.getItem("profile");
        try { _profile = JSON.parse(_profile); } catch { }
        if (_profile?.user_username !== "superadmin" && _profile?.user_username !== "admin") {
            navigate(`/account`);
            return;
        }
        setIsLoading(true);
        await Promise.all([onLoadForms(), onLoadLocations(), onLoadProvinces()]);
        const initial = rangeForPreset("this_month");
        await onFetch({
            evaluation_type: evaluationType,
            limit: pageOption.limit, page: 1,
            formId: "", locationId: "", province: "",
            start: initial.start, end: initial.end,
        });
        setIsLoading(false);
    }

    async function onLoadForms() {
        let response = null;
        try {
            response = await CoreAPI.evaluationFormHttpService.evaluationFormGetListByParam({
                search: "",
                search_by: ["evaluation_name"],
                order: "ASC",
                order_by: "evaluation_name",
                limit: 1000, page: 1,
                type: [], department: [], division: [], province: [], location: [],
            });
        } catch { }
        const rows = Array.isArray(response?.data) ? response.data : [];
        setListForm(rows.map(r => ({
            id: r.evaluation_form_evaluation_form_id,
            label: r.evaluation_form_evaluation_name || "-",
            type: r.evaluation_form_evaluation_type,
        })));
    }

    async function onLoadLocations() {
        let response = null;
        try {
            response = await CoreAPI.locationHttpService.locationList();
        } catch { }
        const rows = Array.isArray(response) ? response : [];
        const sorted = [...rows].sort((a, b) =>
            (a.location_name || "").localeCompare(b.location_name || "", "th"));
        setListLocation(sorted.map(r => ({
            id: r.location_id,
            label: r.location_name || "-",
        })));
    }

    async function onLoadProvinces() {
        let response = [];
        try {
            response = await CoreAPI.locationHttpService.locationProvince();
        } catch { }
        const rows = Array.isArray(response) ? response.map(x => ({
            id: x.location_province,
            label: x.location_province,
        })) : [];
        setListProvince(rows);
    }

    function toStartIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString();
    }
    function toEndIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString();
    }

    async function onFetch({ evaluation_type, limit, page, formId, locationId, province, start, end }) {
        const startIso = start ? toStartIso(start) : toStartIso(rangeForPreset("this_month").start);
        const endIso = end ? toEndIso(end) : toEndIso(rangeForPreset("this_month").end);

        let response = null;
        try {
            response = await CoreAPI.reportHttpService.listRatingSummary({
                evaluation_type: evaluation_type || undefined,
                evaluation_form_id: formId || undefined,
                location_id: locationId || undefined,
                province: province || undefined,
                start_date: startIso,
                end_date: endIso,
                limit, page,
            });
        } catch { }
        if (response) {
            setPageOption({
                limit: response.limit,
                page: response.page,
                total_item: response.total_item,
                total_page: response.total_page,
            });
            setListData(Array.isArray(response.data) ? response.data : []);
        } else {
            setListData([]);
            setPageOption(prev => ({ ...prev, total_item: 0, total_page: 1 }));
        }
    }

    function buildFilterParams(overrides = {}) {
        return {
            evaluation_type: evaluationType,
            limit: pageOption.limit,
            page: pageOption.page,
            formId: (listFormSelect[0]?.id) || "",
            locationId: (listLocationSelect[0]?.id) || "",
            province: (listProvinceSelect[0]?.label) || "",
            start: dateRange.start,
            end: dateRange.end,
            ...overrides,
        };
    }

    async function handleChangeType(typeId) {
        setEvaluationType(typeId);
        setListFormSelect([]);
        setIsLoading(true);
        await onFetch(buildFilterParams({ evaluation_type: typeId, page: 1, formId: "" }));
        setIsLoading(false);
    }

    async function handleChangeForm(picked) {
        setListFormSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, formId: (picked[0]?.id) || "" }));
        setIsLoading(false);
    }
    async function handleChangeLocation(picked) {
        setListLocationSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, locationId: (picked[0]?.id) || "" }));
        setIsLoading(false);
    }
    async function handleChangeProvince(picked) {
        setListProvinceSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, province: (picked[0]?.label) || "" }));
        setIsLoading(false);
    }

    async function handleChangeDateRange(v) {
        setDateRange(v);
        setActivePreset(null);
        if ((v.start && v.end) || (!v.start && !v.end)) {
            setIsLoading(true);
            await onFetch(buildFilterParams({ page: 1, start: v.start, end: v.end }));
            setIsLoading(false);
        }
    }

    async function handlePreset(preset) {
        const range = rangeForPreset(preset);
        setDateRange(range);
        setActivePreset(preset);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, start: range.start, end: range.end }));
        setIsLoading(false);
    }

    async function handleChangePageOption(param) {
        setIsLoading(true);
        await onFetch(buildFilterParams({ limit: param.limit, page: param.page }));
        setIsLoading(false);
    }

    async function handleExport() {
        setIsExporting(true);
        try {
            const startIso = dateRange.start ? toStartIso(dateRange.start) : toStartIso(rangeForPreset("this_month").start);
            const endIso = dateRange.end ? toEndIso(dateRange.end) : toEndIso(rangeForPreset("this_month").end);
            const formId = (listFormSelect[0]?.id) || "";
            const locationId = (listLocationSelect[0]?.id) || "";
            const province = (listProvinceSelect[0]?.label) || "";
            const res = await CoreAPI.reportHttpService.exportRatingSummary({
                evaluation_type: evaluationType || undefined,
                evaluation_form_id: formId || undefined,
                location_id: locationId || undefined,
                province: province || undefined,
                start_date: startIso,
                end_date: endIso,
            });
            const blob = res?.data;
            const cd = res?.headers?.["content-disposition"] || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = (match && match[1]) || `sat-rating-summary.xlsx`;
            triggerBlobDownload(blob, filename);
        } catch (err) {
            console.error("export failed", err);
            alert("ดาวน์โหลดล้มเหลว กรุณาลองใหม่อีกครั้ง");
        }
        setIsExporting(false);
    }

    async function handleExportPdf() {
        if (isExportingPdf || listData.length === 0) return;
        setIsExportingPdf(true);

        const startIso = dateRange.start ? toStartIso(dateRange.start) : toStartIso(rangeForPreset("this_month").start);
        const endIso = dateRange.end ? toEndIso(dateRange.end) : toEndIso(rangeForPreset("this_month").end);
        const formId = (listFormSelect[0]?.id) || "";
        const locationId = (listLocationSelect[0]?.id) || "";
        const province = (listProvinceSelect[0]?.label) || "";
        const params = {
            evaluation_type: evaluationType || undefined,
            evaluation_form_id: formId || undefined,
            location_id: locationId || undefined,
            province: province || undefined,
            start_date: startIso,
            end_date: endIso,
        };

        try {
            const previewRes = await CoreAPI.reportHttpService.previewRatingSummaryPdf(params);
            const data = previewRes?.data ?? previewRes;
            const { location_count, estimated_pages, estimated_seconds } = data || {};

            if (!location_count || location_count === 0) {
                alert("ไม่มีข้อมูลในช่วงที่เลือก");
                setIsExportingPdf(false);
                return;
            }

            const proceed = async () => {
                try {
                    const res = await CoreAPI.reportHttpService.exportRatingSummaryPdf(params);
                    const blob = res?.data;
                    const cd = res?.headers?.["content-disposition"] || "";
                    const match = cd.match(/filename="?([^"]+)"?/i);
                    const filename = (match && match[1]) || `sat-rating-summary.pdf`;
                    triggerBlobDownload(blob, filename);
                } catch (err) {
                    console.error("export pdf failed", err);
                    alert("ดาวน์โหลดล้มเหลว กรุณาลองใหม่อีกครั้ง");
                } finally {
                    setIsExportingPdf(false);
                }
            };

            if (estimated_pages > 100 || estimated_seconds > 60) {
                setSizeWarning({
                    location_count,
                    estimated_pages,
                    estimated_seconds,
                    onConfirm: async () => {
                        setSizeWarning(null);
                        await proceed();
                    },
                    onCancel: () => {
                        setSizeWarning(null);
                        setIsExportingPdf(false);
                    },
                });
                return;
            }

            await proceed();
        } catch (err) {
            console.error("preview failed", err);
            alert("ตรวจสอบขนาดไฟล์ล้มเหลว กรุณาลองใหม่อีกครั้ง");
            setIsExportingPdf(false);
        }
    }

    function triggerBlobDownload(blob, filename) {
        if (!(blob instanceof Blob)) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    function renderRow(row, index) {
        const rank = (pageOption.page - 1) * pageOption.limit + index + 1;
        const fmt = (n) => (n == null ? "-" : Number(n).toFixed(2));
        return (
            <div key={`${row.location_name}-${index}`} className="row">
                <div>{rank}</div>
                <div>{row.location_name || "-"}</div>
                <div className="cell-count">{row.submission_count}</div>
                <div>{fmt(row.avg_satisfaction)}</div>
                <div>{fmt(row.avg_expectation)}</div>
            </div>
        );
    }

    return (
        <BackOfficeLayout>
            <LoadingModal open={isLoading} />
            <Container>
                <div className="header-box">
                    <div className="header-label">
                        สรุปผลการประเมินรายสถานที่ {pageOption.total_item !== 0 ? `(${pageOption.total_item})` : ""}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            className="export-btn"
                            onClick={handleExport}
                            disabled={isExporting || listData.length === 0}
                        >
                            {isExporting ? "กำลังดาวน์โหลด..." : "Export Excel"}
                        </button>
                        <button
                            className="export-btn"
                            onClick={handleExportPdf}
                            disabled={isExportingPdf || listData.length === 0}
                        >
                            {isExportingPdf ? "กำลังดาวน์โหลด..." : "Export PDF"}
                        </button>
                    </div>
                </div>

                <div className="type-tabs">
                    {TYPE_LIST.map(t => (
                        <div
                            key={t.id}
                            className={`type-tab ${evaluationType === t.id ? "active" : ""}`}
                            onClick={() => handleChangeType(t.id)}
                        >
                            {t.label}
                        </div>
                    ))}
                </div>

                <div className="filter-bar">
                    <div className="filters-row">
                        <SearchableField
                            label={"แบบสอบถาม"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listForm.filter(f => f.type === evaluationType)}
                            value={listFormSelect[0] || null}
                            onChange={(picked) => handleChangeForm(picked ? [picked] : [])}
                        />
                        <SearchableField
                            label={"จังหวัด"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listProvince}
                            value={listProvinceSelect[0] || null}
                            onChange={(picked) => handleChangeProvince(picked ? [picked] : [])}
                        />
                        <SearchableField
                            label={"สถานที่"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listLocation}
                            value={listLocationSelect[0] || null}
                            onChange={(picked) => handleChangeLocation(picked ? [picked] : [])}
                        />
                        <DateRangePickerComponent
                            label={"ช่วงวันที่"}
                            placeholder={"เลือกช่วงวันที่"}
                            value={dateRange}
                            onChange={handleChangeDateRange}
                        />
                    </div>
                    <div className="preset-row">
                        <span className="preset-label">เลือกช่วงด่วน:</span>
                        {[
                            { id: "this_month", label: "เดือนนี้" },
                            { id: "last_month", label: "เดือนที่แล้ว" },
                            { id: "this_year", label: "ปีนี้" },
                            { id: "last_year", label: "ปีที่แล้ว" },
                        ].map(p => (
                            <button
                                key={p.id}
                                className={`preset-btn ${activePreset === p.id ? "active" : ""}`}
                                onClick={() => handlePreset(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="data-box">
                    <Table>
                        <div className="row header">
                            <div>#</div>
                            <div>สถานที่</div>
                            <div>จำนวนการตอบ</div>
                            <div>คะแนนความพึงพอใจเฉลี่ย</div>
                            <div>คะแนนความคาดหวังเฉลี่ย</div>
                        </div>
                        {listData.length === 0 && !isLoading && (
                            <div className="empty-box">ไม่พบข้อมูล</div>
                        )}
                        {listData.map(renderRow)}
                    </Table>
                    <PaginatorComponent
                        limit={pageOption.limit}
                        page={pageOption.page}
                        total_item={pageOption.total_item}
                        total_page={pageOption.total_page}
                        onChangePageOption={handleChangePageOption}
                    />
                </div>
            </Container>
            <Dialog open={Boolean(sizeWarning)} onClose={() => sizeWarning?.onCancel?.()}>
                <DialogTitle>การส่งออก PDF ขนาดใหญ่</DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: 8 }}>
                        มี {sizeWarning?.location_count} สถานที่
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        คาดว่าประมาณ {sizeWarning?.estimated_pages} หน้า
                    </div>
                    <div>
                        ใช้เวลาประมาณ {sizeWarning?.estimated_seconds} วินาที
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => sizeWarning?.onCancel?.()}>ยกเลิก</Button>
                    <Button onClick={() => sizeWarning?.onConfirm?.()} variant="contained">
                        ดาวน์โหลดต่อ
                    </Button>
                </DialogActions>
            </Dialog>
        </BackOfficeLayout>
    );
}

export default RatingSummaryPage;
